import {Injectable} from '@angular/core';
import {config} from 'aws-sdk/global';
import {I18n} from 'aws-amplify/utils';
import {isValidRole, Role, TeamAuthorization, User} from "../model/user";
import {LoadingService} from "./loading.service";
import {ApiService} from "./api.service";
import {vocabularies} from "../translations/translations";
import * as Sentry from "@sentry/angular";
import {OfficialTeam, TeamRelatedAccount} from "../model/resources/team";
import {
  confirmResetPassword,
  confirmSignIn,
  confirmUserAttribute,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  sendUserAttributeVerificationCode,
  signIn,
  SignInOutput,
  signOut
} from 'aws-amplify/auth';

I18n.putVocabularies(vocabularies);
I18n.setLanguage('ja');

interface AuthBasicResult {
  isSuccess: boolean;
  message?: string;
}

interface LoginResult {
  isSuccess: boolean;
  message?: string;
  user?: User;
}

interface CompleteNewPasswordResult {
  isSuccess: boolean;
  message?: string;
  user?: User;
}

interface VerifyEmailResult {
  isSuccess: boolean;
  message: string;
  user?: User;
}

export const NEW_PASSWORD_REQUIRED = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  user: User | null = null;
  relatedAccounts: TeamRelatedAccount[] | null = null;

  constructor(private apiService: ApiService,
              private loadingService: LoadingService) {
    setInterval(async () => {
      if (await this.isLoggedIn()) {
        console.log('Refresh session');
        await this.loginByCache(true);
      }
    }, 1000 * 60 * 55);
  }

  async login(email: string, password: string): Promise<LoginResult> {
    this.loadingService.show();
    try {
      const signInOutput: SignInOutput = await signIn({username: email, password});
      if (signInOutput.nextStep.signInStep === NEW_PASSWORD_REQUIRED) {
        return {isSuccess: false, message: NEW_PASSWORD_REQUIRED};
      }

      if (await this.setUserAttributes()) {
        config.credentials = (await fetchAuthSession()).credentials;
        return {isSuccess: true, user: this.user!};
      }
      return {isSuccess: false, message: 'メールアドレスまたはパスワードが正しくありません'};
    } catch (e: any) {
      console.log(e);
      return {isSuccess: false, message: I18n.get(e.message)};
    } finally {
      this.loadingService.hide();
    }
  }

  async completeNewPassword(newPassword: string): Promise<CompleteNewPasswordResult> {
    this.loadingService.show();
    try {
      await confirmSignIn({
        challengeResponse: newPassword,
      });
      if (await this.setUserAttributes()) {
        config.credentials = (await fetchAuthSession()).credentials;
        return {isSuccess: true, user: this.user!};
      }

      return {isSuccess: false, message: 'パスワードの変更に失敗しました'};
    } catch (e: any) {
      Sentry.captureException(e);
      // Auth.completeNewPasswordでエラーが発生した際はこちらに来る
      return {isSuccess: false, message: I18n.get(e.message)};
    } finally {
      this.loadingService.hide();
    }
  }

  async loginByCache(isBackGround?: boolean): Promise<boolean> {
    if (!isBackGround) {
      this.loadingService.show();
    }
    try {
      const session = await fetchAuthSession();
      if (session.credentials) {
        config.credentials = session.credentials;
        return true;
      }
      return false;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      if (!isBackGround) {
        this.loadingService.hide();
      }
    }
  }

  async logout(): Promise<boolean> {
    this.loadingService.show();
    try {
      await signOut({global: true});
      this.user = null;
      this.relatedAccounts = null;
      return true;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    } finally {
      this.loadingService.hide();
    }
  }

  async forgotPassword(email: string): Promise<AuthBasicResult> {
    this.loadingService.show();
    try {
      await resetPassword({username: email});
      return {
        isSuccess: true,
      };
    } catch (e: any) {
      Sentry.captureException(e);
      return {
        isSuccess: false,
        message: I18n.get(e.message),
      };
    } finally {
      this.loadingService.hide();
    }
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<AuthBasicResult> {
    this.loadingService.show();
    try {
      await confirmResetPassword({username: email, confirmationCode: code, newPassword});
      return {
        isSuccess: true,
      }
    } catch (e: any) {
      Sentry.captureException(e);
      return {
        isSuccess: false,
        message: I18n.get(e.message),
      };
    } finally {
      this.loadingService.hide();
    }
  }

  async isLoggedIn(): Promise<boolean> {
    if (this.user != null) {
      return true;
    }

    if (await this.setUserAttributes()) {
      return this.user != null;
    }

    return false;
  }

  isValidateUrlTeamId(urlTeamId: number | undefined): boolean {
    if (!!this.user && this.user.teamAuthorization === undefined) {
      return true;
    } else if (!!this.user && this.user.teamAuthorization !== undefined &&
      (this.user.teamAuthorization.adminTeamIds.find(adminTeamId => adminTeamId === urlTeamId) ||
        this.user.teamAuthorization.ambassadorTeamIds.find(ambassadorTeamId => ambassadorTeamId === urlTeamId))) {
      return true;
    }
    return false;
  }

  async updateTeamAccount(teamId: number, email: string, denyNotifyFeed: boolean, denyNotifyImportant: boolean, userId?: number, hideLoading?: boolean): Promise<boolean> {
    try {
      await this.apiService.patch(teamId, `account`, {email, denyNotifyFeed, denyNotifyImportant, userId}, hideLoading);
      return true;
    } catch (e) {
      return false;
    }
  }

  async verifyEmail(code: string): Promise<VerifyEmailResult> {
    let user: User;
    this.loadingService.show();

    if (await this.setUserAttributes()) {
      user = this.user!;
    } else {
      this.loadingService.hide();

      // ログインチェック判定後にこの関数を呼び出しているのでここのエラーは通常発生しないはず
      return {
        isSuccess: false,
        message: '認証に失敗しました。一度新しいメールアドレスでログインしてからお試しください',
      };
    }

    if (user.emailVerified) {
      this.loadingService.hide();

      return {
        isSuccess: true,
        message: 'すでに認証が完了しています。このままご利用ください。',
        user: user,
      };
    }

    try {
      await confirmUserAttribute({
        userAttributeKey: 'email',
        confirmationCode: code
      });
      return {
        isSuccess: true,
        message: '認証が正常に完了しました。',
        user: user,
      };
    } catch (e) {
      Sentry.captureException(e);
      return {
        isSuccess: false,
        message: '認証に失敗しました。認証コードが古い可能性がありますので最新の認証メールURLからアクセスしたのかどうかご確認の上、再度お試しください。\n\nもし解決しない場合は営業担当にお問い合わせください。',
      }
    } finally {
      this.loadingService.hide();
    }
  }

  async resendVerifyEmail(): Promise<boolean> {
    this.loadingService.show();

    try {
      await sendUserAttributeVerificationCode({userAttributeKey: 'email'});
      return true;
    } catch (e) {
      Sentry.captureException(e);
      return false;
    } finally {
      this.loadingService.hide();
    }
  }

  async getUser(): Promise<User | null> {
    if (await this.setUserAttributes()) {
      return this.user;
    } else {
      return null;
    }
  }

  private async setUserAttributes(): Promise<boolean> {
    try {
      const attrs = await fetchUserAttributes();
      if (!attrs.email || !attrs.name || !attrs['custom:role']) {
        return false;
      }
      const role = attrs['custom:role'] as Role;
      let teamAuth: TeamAuthorization | undefined;

      if (role !== Role.TeamAdmin) {
        teamAuth = undefined;
      } else {
        teamAuth = attrs['custom:teamAuth'] ? JSON.parse(attrs['custom:teamAuth']) : undefined
        if (!teamAuth) {
          const teamId: number | undefined = attrs['custom:teamId'] ? Number(attrs['custom:teamId']) : undefined;
          if (teamId !== undefined) {
            teamAuth = {
              adminTeamIds: [teamId],
              ambassadorTeamIds: []
            }
          }
        }
      }
      if (isValidRole(role)) {
        this.user = new User(attrs.email, attrs.email_verified === 'true', attrs.name, role, teamAuth);
        return true;
      }

      return false;
    } catch (e) {
      console.log(e)
      return false;
    }
  }

  isTattaAdminUser(): boolean {
    switch (this.user?.role) {
      case Role.Admin:
      case Role.Developer:
        return true;
      case Role.Viewer:
      case Role.Staff:
      case Role.TeamAdmin:
        return false;
      default:
        return false;
    }
  }

  isContractor(team: OfficialTeam): boolean {
    return team.paidAccount.email === this.user?.email
  }

  isAdministrator(team: OfficialTeam): boolean {
    if (this.isTattaAdminUser() || this.isContractor(team) || !this.user || !this.user.teamAuthorization) {
      return false;
    }
    return !!this.user.teamAuthorization.adminTeamIds.find(adminTeamId => adminTeamId === team.id);
  }

  isAmbassador(team: OfficialTeam): boolean {
    if (this.isTattaAdminUser() || this.isAdministrator(team) || !this.user || !this.user.teamAuthorization) {
      return false;
    }
    return !!this.user.teamAuthorization.ambassadorTeamIds.find(ambassadorTeamId => ambassadorTeamId === team.id);
  }

  setRelatedAccounts(relatedAccounts: TeamRelatedAccount[]): void {
    this.relatedAccounts = relatedAccounts;
  }

  getLinkedUserId(team: OfficialTeam): number | undefined {
    if (this.isContractor(team)) {
      return team.paidAccount.userId;
    }
    if (!this.relatedAccounts) {
      return;
    }
    const self = this.relatedAccounts.find(account => account.email === this.user?.email);
    if (!self) {
      return;
    }
    return self.userId;
  }
}
